import { rAPI } from './rAPI';
import { inspectionsReportsAPI } from './inspectionsReportsAPI';
import {
  ShareReportResponse,
  ShareReportRequest,
  BulkIssueResponse,
  BulkUpdateItemResponse,
} from './types';

// This returns the same API slice reference but with updated types
export const reportAPISlice = rAPI
  .enhanceEndpoints({
    addTagTypes: ['ReportIssue', 'ReportLink'],
  })
  .injectEndpoints({
    overrideExisting: false,
    // Add new tag types

    endpoints: (builder) => ({
      getReportIssues: builder.query({
        query: (revisionId) => `/report-revisions/${revisionId}/issues`,
        providesTags: ['ReportIssue'],
      }),
      addReportIssue: builder.mutation({
        query: ({ revisionId, issueData }) => ({
          url: `/report-revisions/${revisionId}/issues`,
          method: 'POST',
          body: issueData,
        }),
        invalidatesTags: ['ReportIssue'],
      }),
      updateReportIssue: builder.mutation({
        query: ({ revisionId, issueId, permissions }) => ({
          url: `/report-revisions/${revisionId}/issues/${issueId}`,
          method: 'PUT',
          body: permissions,
        }),
        invalidatesTags: ['ReportIssue'],
      }),
      deleteReportIssue: builder.mutation({
        query: ({ revisionId, issueId }) => ({
          url: `/report-revisions/${revisionId}/issues/${issueId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ReportIssue'],
      }),

      // Shareable link endpoints
      createShareableLink: builder.mutation({
        query: ({ revisionId }: { revisionId: number }) => ({
          url: `/report-revisions/${revisionId}/links`,
          method: 'POST',
        }),
        invalidatesTags: ['ReportLink', { type: 'ReportRevision', id: 'LIST' }],
      }),
      toggleLinkStatus: builder.mutation({
        query: ({ revisionId, linkId, isEnabled, permissions }) => ({
          url: `/report-revisions/${revisionId}/links/${linkId}`,
          method: 'PUT',
          body: {
            is_enabled: isEnabled,
            allow_share: permissions?.allow_share,
            allow_export: permissions?.allow_export,
          },
        }),
        invalidatesTags: ['ReportLink', { type: 'ReportRevision', id: 'LIST' }],
      }),
      createTokenForReport: builder.mutation({
        query: ({ uuid, name, email }) => ({
          url: `/report/view/${uuid}`,
          method: 'POST',
          body: { name, email },
        }),
      }),
      bulkUpdateTestItems: builder.mutation<
        BulkUpdateItemResponse[],
        {
          reportId?: number;
          testItems: number[];
          action: 'status' | 'exclude' | 'include' | 'fastfill';
          status?: 'Draft' | 'Qa' | 'Published' | 'Archived';
        }
      >({
        query: ({
          reportId,
          testItems,
          action,
          status,
        }) => {
          const url = reportId
            ? `/reports/${reportId}/test-items/bulk-update`
            : '/test-items/bulk-update';
          return {
            url,
            method: 'PUT',
            body: { test_items: testItems, action, status },
          };
        },
        invalidatesTags: (result, error, { reportId }) => {
          const reportTags = reportId
            ? [{ type: 'TestItems' as const, id: reportId }]
            : [];

          const itemTags = result
            ? result.map(({ id }) => ({
                type: 'TestItem' as const,
                id,
                ...(reportId && { reportId }),
              }))
            : [];

          return [
            ...itemTags,
            ...reportTags,
            { type: 'TestItems' as const, id: 'LIST' },
          ];
        },
      }),
      withdrawRevision: builder.mutation({
        query: ({
          revision_id,
          reissue,
          replacement_id,
        }: {
          revision_id: number;
          reissue: boolean;
          replacement_id: number;
        }) => ({
          url: `report/revisions/${revision_id}/withdraw`,
          method: 'POST',
          body: { reissue, replacement_id },
        }),
        invalidatesTags: (result, error, { revision_id }) => [
          { type: 'ReportRevision', id: revision_id },
          { type: 'ReportRevision', id: 'LIST' },
          { type: 'Reports', id: 'LIST' },
        ],
      }),
      saveReportLink: builder.mutation({
        query: (revisionId: number) => ({
          url: `report-revisions/${revisionId}/save`,
          method: 'POST',
        }),
        async onQueryStarted(revisionId, { dispatch, queryFulfilled }) {
          // Optimistically update the getPublishedReport cache
          const patchResult = dispatch(
            inspectionsReportsAPI.util.updateQueryData(
              'getPublishedReport',
              revisionId,
              (draft) => {
                if (draft.permissions) {
                  draft.permissions.isSaved = true;
                }
              }
            )
          );

          try {
            await queryFulfilled;
          } catch {
            // If the mutation fails, revert the optimistic update
            patchResult.undo();
          }
        },
      }),

      // Remove from saved list
      removeSavedLink: builder.mutation({
        query: (revisionId: number) => ({
          url: `report-revisions/${revisionId}/save`,
          method: 'DELETE',
        }),
      }),

      // Share a report link with another user
      shareReportLink: builder.mutation({
        query: ({
          revisionId,
          user_id,
          allow_share,
          allow_export,
        }: {
          revisionId: number;
          user_id: number;
          allow_share: boolean;
          allow_export: boolean;
        }) => ({
          url: `report-revisions/${revisionId}/share`,
          method: 'POST',
          body: {
            user_id,
            allow_share,
            allow_export,
          },
        }),
      }),

      // Get user's saved report links
      getSavedLinks: builder.query({
        query: () => ({
          url: 'report-links/saved',
          method: 'GET',
        }),
        // providesTags: ['ReportLink'],
      }),

      shareReport: builder.mutation<
        ShareReportResponse,
        { revisionId: string; data: ShareReportRequest }
      >({
        query: ({ revisionId, data }) => ({
          url: `/reports/revisions/${revisionId}/share`,
          method: 'POST',
          body: data,
        }),
        // Invalidate relevant queries when a report is shared
        invalidatesTags: (result, error, { revisionId }) => [
          { type: 'Report', id: revisionId },
          // 'ReportShares'
        ],
      }),

      bulkIssue: builder.mutation<
        BulkIssueResponse,
        {
          revisionId: number;
          recipients: {
            user_id: number;
            allow_share: boolean;
            allow_export: boolean;
          }[];
          action?: 'keep' | 'withdraw' | 'supersede';
          reissue_shared?: boolean;
          dry_run?: boolean;
        }
      >({
        query: ({
          revisionId,
          recipients,
          action,
          dry_run,
          reissue_shared,
        }) => ({
          url: `/report-revisions/${revisionId}/bulk-issue`,
          method: 'POST',
          body: { recipients, action, dry_run, reissue_shared },
        }),
        invalidatesTags: (result, error, { revisionId, dry_run }) => {
          // Skip cache invalidation if this is a dry run
          if (dry_run) return [];

          return [
            { type: 'ReportRevision', id: revisionId },
            { type: 'ReportRevision', id: 'LIST' },
          ];
        },
      }),

      supersede: builder.mutation<
        void,
        { revisionId: string; replacementId: string }
      >({
        query: ({ revisionId, replacementId }) => ({
          url: `report/revisions/${revisionId}/supersede`,
          method: 'POST',
          body: {
            replacement_id: replacementId,
          },
        }),
        // Optionally invalidate the revisions list cache after superseding
        invalidatesTags: (result, error, { revisionId }) => [
          { type: 'ReportRevision', id: revisionId.toString() },
          { type: 'ReportRevision', id: 'LIST' },
        ],
      }),

      generateReportPdf: builder.mutation<
        { message: string; export_id?: number },
        { revisionId: number; mode?: 'full' | 'results_only' }
      >({
        query: ({ revisionId, mode = 'full' }) => ({
          url: `/reports/published/${revisionId}/generate-pdf`,
          method: 'POST',
          body: { mode },
        }),
      }),

      exportReportRevision: builder.mutation<
        { message: string } | Blob,
        { revisionId: number; sync?: boolean }
      >({
        queryFn: async ({ revisionId, sync = false }, _queryApi, _extraOptions, baseQuery) => {
          const url = sync
            ? `/report-revisions/${revisionId}/export?sync=true`
            : `/report-revisions/${revisionId}/export`
          
          if (sync) {
            // For sync mode, handle blob response manually
            try {
              const state = _queryApi.getState() as any
              const token = state.auth.user?.token || state.sharedReports.accessToken
              const csrf = state.auth.xsrfToken
              
              const API_URL = (import.meta.env.VITE_API_URL as string) + '/api/v1'
              const response = await fetch(`${API_URL}${url}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                  ...(csrf && { 'X-XSRF-TOKEN': csrf }),
                },
                credentials: 'include',
              })

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                return {
                  error: {
                    status: response.status,
                    data: errorData,
                  },
                }
              }

              const blob = await response.blob()
              return { data: blob as Blob }
            } catch (error: any) {
              return {
                error: {
                  status: 'FETCH_ERROR',
                  error: String(error),
                },
              }
            }
          } else {
            // For async mode, use standard baseQuery
            const result = await baseQuery({
              url,
              method: 'POST',
            })
            
            // Type assertion to match expected return type
            if (result.error) {
              return result as { error: typeof result.error }
            }
            
            return {
              data: result.data as { message: string },
            }
          }
        },
      }),

      exportReportRevisionWord: builder.mutation<
        { message: string; export_id?: number },
        { revisionId: number; groupBy: 'location' | 'test' | 'element_group'; source?: 'revision' | 'published' | 'live'; report_id?: number }
      >({
        queryFn: async ({ revisionId, groupBy, source, report_id }, _queryApi, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `/report-revisions/${revisionId}/export/word`,
            method: 'POST',
            body: {
              group_by: groupBy,
              ...(source && { source }),
              ...(report_id && { report_id }),
            },
          })

          if (result.error) {
            return result as { error: typeof result.error }
          }

          // Handle 202 response with export_id
          const data = result.data as { message: string; export_id?: number }
          return { data }
        },
      }),

      getReportExportStatus: builder.query<
        {
          id: number
          status: string
          display_name: string | null
          download_url: string | null
          created_at: string
          updated_at: string
        },
        number
      >({
        query: (exportId) => `/report-exports/${exportId}`,
        transformResponse: (response: { data: any }) => response.data,
      }),

      // Add more mutations here
    }),
  });

export const {
  useGetReportIssuesQuery,
  useAddReportIssueMutation,
  useUpdateReportIssueMutation,
  useDeleteReportIssueMutation,
  useCreateTokenForReportMutation,
  useBulkUpdateTestItemsMutation,
  useWithdrawRevisionMutation,
  // Shareable link endpoints
  useCreateShareableLinkMutation,
  useToggleLinkStatusMutation,
  useSaveReportLinkMutation,
  useRemoveSavedLinkMutation,
  //new api for share report
  useShareReportLinkMutation,
  useGetSavedLinksQuery,
  //new api for share report
  useShareReportMutation,
  useBulkIssueMutation,
  useSupersedeMutation,
  useGenerateReportPdfMutation,
  useExportReportRevisionMutation,
  useExportReportRevisionWordMutation,
  useGetReportExportStatusQuery,
} = reportAPISlice;
